import type { components } from '../../api/schema'

export type DmpChatSessionFile = components['schemas']['Messages.ChatHistory.ChatSessionFile']
export type DmpChatSessionFileKind = 'input' | 'output'

export interface DmpChatSessionFileMetadata {
  kind: DmpChatSessionFileKind
  path: string
  fileName: string
  reference?: string
}

export interface DmpChatSessionFileNode {
  nodeKind: 'file'
  id: string
  name: string
  fileName: string
  kind: DmpChatSessionFileKind
  path: string
  sizeBytes?: number
  contentType?: string | null
}

export interface DmpChatSessionFileFolderNode {
  nodeKind: 'folder'
  id: string
  name: string
  children: DmpChatSessionFileTreeNode[]
}

export type DmpChatSessionFileTreeNode = DmpChatSessionFileNode | DmpChatSessionFileFolderNode

export interface DmpChatSessionFileTree {
  uploadedFiles: DmpChatSessionFileFolderNode
  artifacts: DmpChatSessionFileFolderNode
}

export interface DmpChatSessionFileDownloadParameters {
  path: {
    organisationId: number
    projectId: number
    chatHistoryId: string
  }
  query: {
    kind: DmpChatSessionFileKind
    path: string
  }
}

export interface DmpChatSessionFileDownloadResponse {
  data?: unknown
  error?: unknown
  response?: Response
}

export function createDmpChatSessionFileDownloadParameters(
  context: DmpChatSessionFileDownloadParameters['path'],
  file: Pick<DmpChatSessionFileNode, 'kind' | 'path'>
): DmpChatSessionFileDownloadParameters {
  return {
    path: context,
    query: {
      kind: file.kind,
      path: file.path,
    },
  }
}

export async function readDmpChatSessionFileBlob(result: DmpChatSessionFileDownloadResponse): Promise<Blob> {
  if (result.data instanceof Blob) {
    return result.data
  }

  if (!result.response?.ok) {
    throw new Error('The file response was invalid.')
  }

  return result.response.blob()
}

export function normalizeDmpChatSessionFile(file: DmpChatSessionFile): DmpChatSessionFileMetadata | null {
  const kind = file.kind?.trim()
  const path = normalizeText(file.path)
  const fileName = normalizeText(file.fileName)
  if ((kind !== 'input' && kind !== 'output') || !path || !fileName) {
    return null
  }

  const reference = normalizeText(kind === 'input' ? file.inputRef : file.outputRef)
  return {
    kind,
    path,
    fileName,
    ...(reference ? { reference } : {}),
  }
}

export function buildDmpChatSessionFileTree(files: readonly DmpChatSessionFile[]): DmpChatSessionFileTree {
  const uploadedFiles = createFolder('folder:uploaded-files', 'Uploaded files')
  const artifacts = createFolder('folder:artifacts', 'Artifacts')
  const usedFileIds = new Set<string>()

  files.forEach((file) => {
    const normalizedFile = normalizeDmpChatSessionFile(file)
    if (!normalizedFile) {
      return
    }

    const { fileName, kind, path, reference } = normalizedFile
    const normalizedSizeBytes = normalizeSizeBytes(file.sizeBytes)

    if (kind === 'input') {
      const inputIdentity = reference ?? path
      uploadedFiles.children.push({
        nodeKind: 'file',
        id: createUniqueFileId(`file:input:${inputIdentity}`, usedFileIds),
        name: fileName,
        fileName,
        kind,
        path,
        ...(normalizedSizeBytes === undefined ? {} : { sizeBytes: normalizedSizeBytes }),
        ...(file.contentType == null ? {} : { contentType: file.contentType }),
      })
      return
    }

    const pathSegments = path.split('/').filter(Boolean)
    const outputRef = pathSegments[0]
    if (!outputRef) {
      return
    }

    let parentFolder = getOrCreateFolder(artifacts, outputRef)
    const nestedFolderSegments = pathSegments.slice(1, -1)
    for (const segment of nestedFolderSegments) {
      parentFolder = getOrCreateFolder(parentFolder, segment)
    }

    parentFolder.children.push({
      nodeKind: 'file',
      id: createUniqueFileId(`file:output:${path}`, usedFileIds),
      name: pathSegments.at(-1) ?? fileName,
      fileName,
      kind,
      path,
      ...(normalizedSizeBytes === undefined ? {} : { sizeBytes: normalizedSizeBytes }),
      ...(file.contentType == null ? {} : { contentType: file.contentType }),
    })
  })

  return { uploadedFiles, artifacts }
}

export function filterDmpChatSessionFileTree(
  tree: DmpChatSessionFileTree,
  searchValue: string
): DmpChatSessionFileTree {
  const normalizedSearchValue = searchValue.trim().toLowerCase()
  if (!normalizedSearchValue) {
    return cloneDmpChatSessionFileTree(tree)
  }

  return {
    uploadedFiles: filterRootFolder(tree.uploadedFiles, normalizedSearchValue),
    artifacts: filterRootFolder(tree.artifacts, normalizedSearchValue),
  }
}

export function formatDmpChatFileSize(sizeBytes: number | undefined): string | null {
  if (sizeBytes === undefined || !Number.isFinite(sizeBytes) || sizeBytes < 0) {
    return null
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let value = sizeBytes
  let unitIndex = 0

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024
    unitIndex += 1
  }

  const roundedValue = unitIndex === 0 || value >= 10 ? Math.round(value) : Math.round(value * 10) / 10
  return `${roundedValue} ${units[unitIndex]}`
}

function createFolder(id: string, name: string): DmpChatSessionFileFolderNode {
  return {
    nodeKind: 'folder',
    id,
    name,
    children: [],
  }
}

function getOrCreateFolder(parent: DmpChatSessionFileFolderNode, name: string): DmpChatSessionFileFolderNode {
  const existingFolder = parent.children.find(
    (child): child is DmpChatSessionFileFolderNode => child.nodeKind === 'folder' && child.name === name
  )
  if (existingFolder) {
    return existingFolder
  }

  const folder = createFolder(`${parent.id}/${name}`, name)
  parent.children.push(folder)
  return folder
}

function createUniqueFileId(baseId: string, usedFileIds: Set<string>): string {
  let id = baseId
  let suffix = 2

  while (usedFileIds.has(id)) {
    id = `${baseId}:${suffix}`
    suffix += 1
  }

  usedFileIds.add(id)
  return id
}

function normalizeSizeBytes(sizeBytes: number | undefined): number | undefined {
  return typeof sizeBytes === 'number' && Number.isFinite(sizeBytes) && sizeBytes >= 0 ? sizeBytes : undefined
}

function normalizeText(value: string | null | undefined): string | undefined {
  const normalizedValue = value?.trim()
  return normalizedValue || undefined
}

function filterRootFolder(
  folder: DmpChatSessionFileFolderNode,
  normalizedSearchValue: string
): DmpChatSessionFileFolderNode {
  return filterFolder(folder, normalizedSearchValue) ?? createFolder(folder.id, folder.name)
}

function filterFolder(
  folder: DmpChatSessionFileFolderNode,
  normalizedSearchValue: string
): DmpChatSessionFileFolderNode | null {
  if (folder.name.toLowerCase().includes(normalizedSearchValue)) {
    return cloneFolder(folder)
  }

  const matchingChildren: DmpChatSessionFileTreeNode[] = []
  for (const child of folder.children) {
    if (child.nodeKind === 'file') {
      if (child.name.toLowerCase().includes(normalizedSearchValue)) {
        matchingChildren.push(cloneFile(child))
      }
      continue
    }

    const matchingFolder = filterFolder(child, normalizedSearchValue)
    if (matchingFolder) {
      matchingChildren.push(matchingFolder)
    }
  }

  return matchingChildren.length > 0 ? { ...folder, children: matchingChildren } : null
}

function cloneDmpChatSessionFileTree(tree: DmpChatSessionFileTree): DmpChatSessionFileTree {
  return {
    uploadedFiles: cloneFolder(tree.uploadedFiles),
    artifacts: cloneFolder(tree.artifacts),
  }
}

function cloneFolder(folder: DmpChatSessionFileFolderNode): DmpChatSessionFileFolderNode {
  return {
    ...folder,
    children: folder.children.map((child) => (child.nodeKind === 'folder' ? cloneFolder(child) : cloneFile(child))),
  }
}

function cloneFile(file: DmpChatSessionFileNode): DmpChatSessionFileNode {
  return { ...file }
}
